import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import type { RegionSummary, TensionLevel } from '../../lib/dashboardApi';

// Fix leaflet default marker icons for webpack/vite bundlers
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;

/* ── Props ──────────────────────────────────────────────────── */

interface SenegalMapProps {
  regions: RegionSummary[];
  selectedRegionId: string | null;
  onRegionClick: (regionId: string) => void;
}

/* ── Tension level colors ────────────────────────────────────── */

const TENSION_COLORS: Record<TensionLevel, string> = {
  LOW: '#22c55e',
  MODERATE: '#eab308',
  HIGH: '#f97316',
  CRITICAL: '#ef4444',
};

const TENSION_LABELS: Record<TensionLevel, string> = {
  LOW: 'Faible',
  MODERATE: 'Modere',
  HIGH: 'Eleve',
  CRITICAL: 'Critique',
};

/* ── Region center coordinates ───────────────────────────────── */

const REGION_CENTERS: Record<string, [number, number]> = {
  'dakar': [14.7167, -17.4677],
  'thies': [14.7886, -16.9260],
  'saint-louis': [16.0179, -16.4897],
  'diourbel': [14.6546, -16.2316],
  'fatick': [14.3389, -16.4104],
  'kaolack': [14.1652, -16.0758],
  'kaffrine': [14.1058, -15.5514],
  'kolda': [12.8835, -14.9500],
  'ziguinchor': [12.5681, -16.2719],
  'sedhiou': [12.7081, -15.5569],
  'tambacounda': [13.7709, -13.6673],
  'kedougou': [12.5605, -12.1747],
  'matam': [15.6559, -13.2554],
  'louga': [15.6145, -16.2282],
};

/* ── Circle marker icon factory ──────────────────────────────── */

function createCircleIcon(color: string, isSelected: boolean): L.DivIcon {
  const size = isSelected ? 18 : 14;
  const borderWidth = isSelected ? 3 : 2;
  const borderColor = isSelected ? '#1e293b' : '#ffffff';

  return L.divIcon({
    className: 'senegal-map-circle-marker',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: ${color};
      border: ${borderWidth}px solid ${borderColor};
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/* ── Custom cluster icon factory ─────────────────────────────── */

function createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount();
  let size = 40;
  let fontSize = 13;

  if (count >= 50) {
    size = 56;
    fontSize = 16;
  } else if (count >= 20) {
    size = 48;
    fontSize = 15;
  } else if (count >= 10) {
    size = 44;
    fontSize = 14;
  }

  return L.divIcon({
    html: `
      <div class="marker-cluster-custom-outer" style="
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(34, 197, 94, 0.25);
        border-radius: 50%;
      ">
        <div style="
          width: ${size - 10}px;
          height: ${size - 10}px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #22c55e;
          color: white;
          border-radius: 50%;
          font-weight: 700;
          font-size: ${fontSize}px;
          font-family: 'Inter', system-ui, sans-serif;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        ">${count}</div>
      </div>
    `,
    className: 'marker-cluster-custom',
    iconSize: L.point(size, size),
    iconAnchor: [size / 2, size / 2],
  });
}

/* ── Marker cluster layer (reacts to prop changes) ───────────── */

interface ClusterLayerProps {
  regions: RegionSummary[];
  selectedRegionId: string | null;
  onRegionClick: (regionId: string) => void;
}

function ClusterLayer({ regions, selectedRegionId, onRegionClick }: ClusterLayerProps) {
  const map = useMap();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {
    // Remove previous cluster group if present
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    // Create the cluster group with custom styling
    const clusterGroup = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 9,
      animate: true,
    });

    // Create one marker per region, placed at region center
    for (const region of regions) {
      const coords = REGION_CENTERS[region.regionId.toLowerCase()];
      if (!coords) continue;

      const color = TENSION_COLORS[region.tensionLevel] ?? '#94a3b8';
      const isSelected = region.regionId === selectedRegionId;
      const icon = createCircleIcon(color, isSelected);

      const occupancyPct = (region.occupancyRate * 100).toFixed(1);
      const tensionLabel = TENSION_LABELS[region.tensionLevel] ?? region.tensionLevel;

      // Create individual markers per structure count (for cluster aggregation)
      // But since we only have region-level data, we create one marker per region
      // with a rich popup
      const marker = L.marker([coords[0], coords[1]], { icon });

      marker.bindPopup(
        `<div style="font-family: 'Inter', system-ui, sans-serif; min-width: 180px;">
          <div style="font-weight: 700; font-size: 14px; color: #111827; margin-bottom: 6px;">
            ${region.name}
          </div>
          <div style="display: flex; flex-direction: column; gap: 3px; font-size: 12px; color: #4b5563;">
            <div style="display: flex; justify-content: space-between;">
              <span>Structures</span>
              <span style="font-weight: 600; color: #111827;">${region.structureCount}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Occupation</span>
              <span style="font-weight: 600; color: #111827;">${occupancyPct}%</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Tension</span>
              <span style="font-weight: 600; color: ${color};">${tensionLabel}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Orientations</span>
              <span style="font-weight: 600; color: #111827;">${region.orientationsToday}</span>
            </div>
            ${region.stockAlerts > 0
              ? `<div style="display: flex; justify-content: space-between;">
                  <span>Alertes stock</span>
                  <span style="font-weight: 600; color: #ef4444;">${region.stockAlerts}</span>
                </div>`
              : ''
            }
          </div>
        </div>`,
        { closeButton: true, className: 'senegal-map-popup' }
      );

      marker.bindTooltip(
        `<strong>${region.name}</strong> - ${region.structureCount} structure${region.structureCount > 1 ? 's' : ''}`,
        { direction: 'top', offset: [0, -8] }
      );

      marker.on('click', () => {
        onRegionClick(region.regionId);
      });

      clusterGroup.addLayer(marker);
    }

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    // Cleanup on unmount
    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
      }
    };
  }, [map, regions, selectedRegionId, onRegionClick]);

  return null;
}

/* ── Main component ──────────────────────────────────────────── */

export function SenegalMap({ regions, selectedRegionId, onRegionClick }: SenegalMapProps) {
  return (
    <div style={{ height: '400px', width: '100%' }} className="rounded-lg overflow-hidden border border-slate-200">
      <MapContainer
        center={[14.4, -14.5]}
        zoom={7}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClusterLayer
          regions={regions}
          selectedRegionId={selectedRegionId}
          onRegionClick={onRegionClick}
        />
      </MapContainer>
    </div>
  );
}
